import { useState, useRef, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useSiteLogo } from "@/hooks/useSiteLogo";

export default function AdminLogoPage() {
  const { logoUrl, refetch } = useSiteLogo();
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: "error", text: "파일 크기는 2MB 이하여야 합니다" });
      return;
    }

    if (!["image/png", "image/jpeg", "image/gif", "image/webp"].includes(file.type)) {
      setMessage({ type: "error", text: "PNG, JPEG, GIF, WebP 형식만 지원합니다" });
      return;
    }

    setUploading(true);
    setMessage(null);

    try {
      const { error: uploadError } = await supabase.storage
        .from("admin-assets")
        .upload("logo/site-logo", file, { upsert: true, contentType: file.type });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from("admin-assets").getPublicUrl("logo/site-logo");
      setPreviewUrl(urlData.publicUrl);
      setMessage({ type: "success", text: "로고가 성공적으로 변경되었습니다!" });
      refetch();
    } catch {
      setMessage({ type: "error", text: "로고 업로드에 실패했습니다" });
    } finally {
      setUploading(false);
    }
  }, [refetch]);

  const handleReset = useCallback(async () => {
    setMessage(null);
    try {
      await supabase.storage.from("admin-assets").remove(["logo/site-logo"]);
      setPreviewUrl("");
      setMessage({ type: "success", text: "기본 로고로 복원되었습니다" });
      refetch();
    } catch {
      setMessage({ type: "error", text: "로고 초기화에 실패했습니다" });
    }
  }, [refetch]);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-base font-bold text-foreground-800">로고 관리</h2>
        <p className="text-xs text-foreground-600 mt-0.5">사이트 로고 이미지를 변경할 수 있습니다</p>
      </div>

      {message && (
        <div
          className={`px-4 py-3 rounded-lg text-xs font-medium ${
            message.type === "success"
              ? "bg-accent-100/80 text-accent-800 border border-accent-300/60"
              : "bg-red-500/10 text-red-400 border border-red-500/30"
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current Logo */}
        <div className="bg-background-100 rounded-xl border border-background-300 p-5">
          <h3 className="text-sm font-bold text-foreground-800 mb-4">현재 로고</h3>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-xl overflow-hidden bg-background-200 border border-background-300 flex items-center justify-center">
              <img
                src={previewUrl || logoUrl}
                alt="사이트 로고"
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            </div>
            <div>
              <p className="text-xs text-foreground-700 font-medium mb-1">
                {previewUrl ? "새 로고 미리보기" : "적용된 로고"}
              </p>
              <p className="text-[10px] text-foreground-600">
                권장 크기: 128x128px (정사각형)
              </p>
              <p className="text-[10px] text-foreground-600">
                지원 형식: PNG, JPEG, GIF, WebP (최대 2MB)
              </p>
            </div>
          </div>
        </div>

        {/* Upload */}
        <div className="bg-background-100 rounded-xl border border-background-300 p-5">
          <h3 className="text-sm font-bold text-foreground-800 mb-4">로고 업로드</h3>
          <div className="space-y-4">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full py-3 bg-primary-500 text-foreground-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-upload-cloud-2-line" />
              </span>
              {uploading ? "업로드 중..." : "새 로고 업로드하기"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              onChange={handleUpload}
              className="hidden"
            />

            <button
              onClick={handleReset}
              className="w-full py-2.5 bg-background-200 text-foreground-600 text-xs font-medium rounded-lg hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-1.5"
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-refresh-line" />
              </span>
              기본 로고로 초기화
            </button>
          </div>
        </div>
      </div>

      {/* Preview in header context */}
      <div className="bg-background-100 rounded-xl border border-background-300 p-5">
        <h3 className="text-sm font-bold text-foreground-800 mb-3">헤더 미리보기</h3>
        <div className="bg-background-50 border border-background-300 rounded-lg p-3 flex items-center gap-2">
          <img
            src={previewUrl || logoUrl}
            alt="로고"
            className="w-9 h-9 rounded-lg object-cover"
          />
          <span className="text-lg font-black tracking-tight">
            <span className="text-primary-500">토</span>
            <span className="text-foreground-700">찾사 시즌2</span>
          </span>
          <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold bg-primary-500 text-foreground-50 rounded whitespace-nowrap">
            공식커뮤니티
          </span>
        </div>
      </div>
    </div>
  );
}